import type * as React from "react";

import { cn } from "../lib/cn";

/*
 * Manila is not a token, it is a mix: the folder is card stock soaked in the
 * gold quill color. The tab is the doubled-over edge of the same sheet, so it
 * carries more of the gold (28%) than the body (18%).
 */
const MANILA_TAB =
  "bg-[color-mix(in_srgb,var(--color-gold)_28%,var(--color-card))]";
const MANILA_BODY =
  "bg-[color-mix(in_srgb,var(--color-gold)_18%,var(--color-card))]";

/*
 * The tab is a trapezoid cut with clip-path: sides slanted ~14deg from
 * vertical (9px of run over a one-line tab), top corners softened by a small
 * bevel in the last 5px of each slant. clip-path clips a CSS border away with
 * everything else, so the hairline is layered instead: the outer element IS
 * the trapezoid painted in the rule color, and an absolutely positioned inner
 * element repeats the same polygon 1px smaller in manila. Layering was chosen
 * over dropping the border because manila sits too close to the page color to
 * hold a cut edge on fill contrast alone, and the folder body already wears
 * this hairline - the tab has to look cut from the same sheet. The inner
 * layer runs to the very bottom (no bottom inset) so no stroke crosses the
 * fold where the tab meets the body.
 */
const TAB_SHAPE =
  "[clip-path:polygon(0_100%,9px_15%,14px_0,calc(100%_-_14px)_0,calc(100%_-_9px)_15%,100%_100%)]";

export interface FolderTabProps extends React.HTMLAttributes<HTMLDivElement> {
  /** What is written on the tab. The record's name - usually the patient. */
  tab: React.ReactNode;
  /** Small print next to the name on the tab: species, breed, chart number. */
  tabMeta?: React.ReactNode;
  /** Controls for the whole record, sitting on the desk beside the tab. */
  actions?: React.ReactNode;
  /** The contents of the folder. */
  children: React.ReactNode;
  /**
   * The folder is the screen, not an object on it: it fills the height its
   * container gives it and hands the body's padding and scrolling to whatever
   * is inside, so interior regions can own their own scroll areas. Needs a
   * height from the caller (`h-full`, or `flex-1` in a flex column).
   */
  fill?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * A manila folder with a written tab: the patient record. Everything known
 * about one animal lives inside one of these, and the tab is how you find it
 * in the drawer.
 *
 * The tab is a trapezoid - wider at the base, angled sides, like a real
 * folder tab - and overlaps the folder body by a hairline so the two read as
 * one folded sheet rather than a box with a box on top. Long tab text
 * truncates with an ellipsis instead of colliding with the actions. The
 * folder never rotates - it is a working surface, not a decoration.
 */
export function FolderTab({
  tab,
  tabMeta,
  actions,
  children,
  fill = false,
  className,
  ...rest
}: FolderTabProps) {
  return (
    <div
      className={cn("relative", fill && "flex min-h-0 flex-col", className)}
      {...rest}
    >
      <div className="flex items-end gap-3">
        <div className={cn(TAB_SHAPE, "relative z-10 -mb-px min-w-0 bg-rule")}>
          <div
            aria-hidden="true"
            className={cn(
              TAB_SHAPE,
              MANILA_TAB,
              "absolute inset-x-px top-px bottom-0",
            )}
          />
          <div className="relative flex min-w-0 items-baseline gap-2 px-6 py-2.5">
            <span className="min-w-0 truncate font-display font-medium text-ink">
              {tab}
            </span>
            {tabMeta ? (
              <span className="min-w-0 truncate font-body text-sm font-normal text-annotation">
                {tabMeta}
              </span>
            ) : null}
          </div>
        </div>
        {actions ? (
          <div className="mb-2 ml-auto flex shrink-0 items-center gap-2">
            {actions}
          </div>
        ) : null}
      </div>
      <div
        className={cn(
          MANILA_BODY,
          // top-left corner stays square: that is where the tab folds over
          "rounded-paper rounded-tl-none border border-rule shadow-lift",
          // min-h-0 is what lets a filling body shrink inside the flex column,
          // so the scroll regions inside it are bounded rather than growing.
          fill && "flex min-h-0 flex-1 flex-col",
        )}
      >
        <div className={fill ? "flex min-h-0 flex-1 flex-col" : "p-5"}>
          {children}
        </div>
      </div>
    </div>
  );
}
